'use client';

import {
  Button,
  Checkbox,
  DialogContent,
  Dialogß,
  Switch,
} from '@otter-ai/den-components';
import {
  GeneralToggleSetStateEvent,
  Screen,
  trackEvent,
  UIElementID,
} from '@otter-ai/telemetry';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useUpdateWorkspaceSettingMutation } from '@/query/mutations/useUpdateWorkspaceSettingMutation';
import { queryKey as workspaceQueryKey } from '@/query/workspaceQueryCreator';
import {
  getFromLocalStorage,
  LOCAL_STORAGE_KEYS,
  removeFromLocalStorage,
  setInLocalStorage,
} from '@/shared/utils/localStorageUtils';

const LOCAL_STORAGE_KEY =
  LOCAL_STORAGE_KEYS.HIDE_WORKSPACE_INVITE_SETTING_DIALOG;
type WorkspaceInviteSettingDialogProps = {
  done: () => void;
  workspaceId: number;
  workspaceDomains: string[];
  currentInviteWhenSharing: boolean;
};

function getDomainString(domains: string[] | undefined | null): string {
  return domains?.map((domain) => `@${domain}`).join(', ') || 'your workspace';
}

export function WorkspaceInviteSettingDialog({
  done,
  workspaceId,
  workspaceDomains,
  currentInviteWhenSharing,
}: WorkspaceInviteSettingDialogProps) {
  const t = useTranslations('manage-team:team-profile');
  const queryClient = useQueryClient();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [inviteWhenSharing, setInviteWhenSharing] = useState(
    currentInviteWhenSharing,
  );

  const updateSettingMutation = useUpdateWorkspaceSettingMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  });

  const handleToggle = (checked: boolean) => {
    setInviteWhenSharing(checked);

    // Track toggle state change (matches Angular behavior)
    trackEvent(
      GeneralToggleSetStateEvent({
        UIElementID: UIElementID.workspaceShareInviteToggle,
        IsStateOn: checked,
        Screen: Screen.createTeam,
      }),
    );

    updateSettingMutation.mutate({
      workspace_id: String(workspaceId),
      setting_name: 'invite_when_sharing',
      setting_value_bool: String(checked),
    });
  };

  const handleDontShowAgainChange = (checked: boolean | 'indeterminate') => {
    if (checked === 'indeterminate') return;
    setDontShowAgain(checked);
    if (checked) {
      setInLocalStorage(LOCAL_STORAGE_KEY, 'true');
    } else {
      removeFromLocalStorage(LOCAL_STORAGE_KEY);
    }
  };

  const handleClose = () => {
    done();
  };

  const domainString = getDomainString(workspaceDomains);

  return (
    <Dialogß
      isOpen
      onOpenChange={handleClose}
      size="md"
      data-testid="workspace-invite-setting-dialog"
      title={t('dialogs.workspaceInviteSetting.title')}
    >
      <DialogContent>
        <div className="space-y-5">
          <p className="text-subtle">
            {t('dialogs.workspaceInviteSetting.description')}
          </p>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-stroke p-4">
            <div className="flex flex-1 flex-col gap-2">
              <p className="text-sm font-semibold">
                {t('dialogs.workspaceInviteSetting.autoInviteTitle')}
              </p>
              <p className="text-sm text-subtle">
                {t('dialogs.workspaceInviteSetting.autoInviteDescription', {
                  domains: domainString,
                })}
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center">
              <Switch
                checked={inviteWhenSharing}
                onCheckedChange={handleToggle}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              className="flex cursor-pointer items-center gap-2"
              htmlFor="dont-show-again-checkbox"
            >
              <Checkbox
                id="dont-show-again-checkbox"
                checked={dontShowAgain}
                onCheckedChange={handleDontShowAgainChange}
              />
              <span className="text-sm font-semibold">
                {t('dialogs.workspaceInviteSetting.dontShowAgain')}
              </span>
            </label>
            <Button variant="primary" onClick={handleClose}>
              {t('dialogs.workspaceInviteSetting.done')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialogß>
  );
}

/**
 * Check if the dialog should be shown (user hasn't opted out)
 */
export function shouldShowWorkspaceInviteSettingDialog(): boolean {
  if (typeof window === 'undefined') return true;
  return getFromLocalStorage(LOCAL_STORAGE_KEY) !== 'true';
}
